// /notary/to/$country — deep destination-country pages for Notary Public TH
// Phase B1 — 3,000+ คำต่อหน้า

import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import type { NotaryDestination } from "@/data/notary-destinations";
import { getNotaryDestHead, NOTARY_DEST_HEAD } from "@/data/notary-dest-head.gen";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema } from "@/lib/aeo";
import { ScrollText, MapPin, Phone, MessageCircle, ShieldCheck, Globe2 } from "lucide-react";
import NotaryDestinationDeepContent from "@/components/NotaryDestinationDeepContent";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/notary/to/$country")({
  loader: async ({ params }) => {
    if (!getNotaryDestHead(params.country)) throw notFound();
    const { getNotaryDestination } = await import("@/data/notary-destinations");
    const dest = getNotaryDestination(params.country);
    if (!dest) throw notFound();
    return { dest };
  },
  head: ({ params, loaderData }) => {
    const dest = getNotaryDestHead(params.country);
    if (!dest) return { meta: [{ title: ctaTitle("Notary — ไม่พบประเทศ", "/notary/to/$country", "th")}] };
    const url = `${SITE}/notary/to/${dest.slug}`;
    return {
      meta: [
        { title: ctaTitle(dest.metaTitle, "/notary/to/$country", "th")},
        { name: "description", content: ctaDesc(dest.metaDesc, "/notary/to/$country", "th")},
        { property: "og:title", content: ctaTitle(dest.metaTitle, "/notary/to/$country", "th")},
        { property: "og:description", content: ctaDesc(dest.metaDesc, "/notary/to/$country", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/notary/to/${dest.slug}`),
      ],
      scripts: [
        speakableSchema(),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: dest.metaTitle,
            description: dest.metaDesc,
            inLanguage: "th",
            image: `${SITE}/og-default.jpg`,
            datePublished: "2026-06-16",
            dateModified: "2026-06-16",
            author: { "@type": "Organization", name: "NYC Language Institute", url: SITE },
            publisher: {
              "@type": "Organization",
              name: "NYC Language Institute",
              logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
            },
            mainEntityOfPage: url,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: dest.metaTitle,
            description: dest.metaDesc,
            serviceType: "Notary Public + Apostille/Consular Legalization",
            provider: { "@type": "LegalService", name: "NYC Language Institute", url: SITE },
            areaServed: { "@type": "Country", name: dest.countryEn },
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "THB",
              lowPrice: dest.feeRangeTh.replace(/[^\d–]/g, "").split("–")[0],
              highPrice: dest.feeRangeTh.replace(/[^\d–]/g, "").split("–")[1],
            },
          }),
        },
        ...(loaderData ? [{
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            inLanguage: "th",
            mainEntity: loaderData.dest.faqs.map((f: { q: string; a: string }) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }] : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
              { "@type": "ListItem", position: 2, name: "Notary Public", item: `${SITE}/notary` },
              { "@type": "ListItem", position: 3, name: `สำหรับใช้ใน${dest.countryTh}`, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: NotaryDestinationPage,
});

function NotaryDestinationPage() {
  const { dest } = Route.useLoaderData() as { dest: NotaryDestination };
  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16">
          <nav aria-label="Breadcrumb" className="mb-4 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/notary" className="hover:text-gold">Notary Public</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">สำหรับใช้ใน{dest.countryTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ScrollText className="h-3.5 w-3.5" /> Notary + {dest.legalisationRoute === "apostille" ? "Apostille (Hague)" : "Consular Legalization"}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            <span className="mr-3 text-4xl sm:text-5xl">{dest.flag}</span>
            Notary Public สำหรับใช้ใน<span className="text-gold">{dest.countryTh}</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{dest.heroIntro}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-[13px]">
            <div className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
              <div className="text-white/60 text-[10px] font-bold uppercase tracking-widest">ราคาเริ่ม</div>
              <div className="mt-1 font-bold text-gold">{dest.feeRangeTh}</div>
            </div>
            <div className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
              <div className="text-white/60 text-[10px] font-bold uppercase tracking-widest">ระยะเวลา</div>
              <div className="mt-1 font-bold">{dest.turnaroundDays}</div>
            </div>
            <div className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
              <div className="text-white/60 text-[10px] font-bold uppercase tracking-widest">เส้นทาง</div>
              <div className="mt-1 font-bold">{dest.legalisationRoute === "apostille" ? "Apostille (MFA)" : "MFA + สถานทูต"}</div>
            </div>
            <div className="rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
              <div className="text-white/60 text-[10px] font-bold uppercase tracking-widest">ภาษาที่ยอมรับ</div>
              <div className="mt-1 font-bold text-[12px]">{dest.acceptedLanguages.join(", ")}</div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      {/* Common Acts */}
      <section className="mx-auto max-w-5xl px-4 sm:px-6 mt-10">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/50 p-6">
          <h2 className="font-display text-xl font-extrabold text-navy-deep flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            ประเภทเอกสาร Notary ยอดนิยมสำหรับ{dest.countryTh}
          </h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {dest.commonActs.map((a) => (
              <li key={a} className="flex items-start gap-2 text-[14px] text-slate-700">
                <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-gold" />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Deep Sections */}
      <article className="mx-auto max-w-5xl px-4 sm:px-6 py-10 sm:py-12 space-y-10">
        {dest.sections.map((sec) => (
          <section key={sec.h2}>
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{sec.h2}</h2>
            <div className="mt-4 space-y-4 text-[15px] leading-[1.85] text-slate-700">
              {sec.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>
        ))}

        {/* Embassy info */}
        {dest.embassyAddress && (
          <section className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
            <h2 className="font-display text-xl font-extrabold text-navy-deep flex items-center gap-2">
              <MapPin className="h-5 w-5 text-amber-600" /> {dest.embassyTh}
            </h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 text-[14px] text-slate-700">
              <div><span className="font-bold text-navy-deep">ที่อยู่:</span> {dest.embassyAddress}</div>
              {dest.embassyPhone && <div><span className="font-bold text-navy-deep">โทรศัพท์:</span> {dest.embassyPhone}</div>}
            </div>
          </section>
        )}

        {/* FAQ */}
        <section>
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 space-y-4">
            {dest.faqs.map((f, i) => (
              <details key={i} className="group rounded-xl border border-slate-200 bg-white p-4 open:bg-slate-50/60">
                <summary className="cursor-pointer font-bold text-navy-deep group-open:text-gold">{f.q}</summary>
                <p className="mt-2 text-[14px] leading-[1.8] text-slate-700">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related countries */}
        <section className="rounded-2xl border border-slate-200 bg-slate-50/50 p-6">
          <h2 className="font-display text-xl font-extrabold text-navy-deep flex items-center gap-2">
            <Globe2 className="h-5 w-5 text-slate-600" /> Notary สำหรับประเทศอื่น
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {NOTARY_DEST_HEAD.filter((d) => d.slug !== dest.slug).map((d) => (
              <Link key={d.slug} to="/notary/to/$country" params={{ country: d.slug }}
                className="rounded-full bg-white px-3 py-1.5 text-[12px] font-semibold text-navy-deep ring-1 ring-slate-300 hover:bg-slate-100 hover:ring-gold">
                {d.flag} {d.countryTh}
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="rounded-3xl border border-gold/40 bg-gradient-to-br from-gold/15 via-white/5 to-transparent p-6 sm:p-8 text-center">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep">
            พร้อมเริ่มทำ Notary สำหรับใช้ใน{dest.countryTh}แล้ว?
          </h2>
          <p className="mt-2 text-[14px] text-slate-700">
            ทักไลน์ @nycli หรือโทร 083-249-4999 — ทีมแจ้งราคาและไทม์ไลน์เบ็ดเสร็จภายใน 30 นาที
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-navy-deep px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </section>
      </article>
      <NotaryDestinationDeepContent
        countryTh={dest.countryTh}
        countryEn={dest.countryEn}
        route={dest.legalisationRoute}
        lang="th"
      />
    </main>
  );
}
